/**
 * P3 — per-token row status. Reads the current account's snapshot and returns the
 * chain-level status for a given chainId. Returns `null` when the feature flag is
 * off, no snapshot exists, or the chain isn't tracked in the snapshot yet.
 *
 * Callers should render a small icon (scanning/stale/error/poisoned) next to the
 * token row when the result is non-null; an `ok` result is intentionally still
 * returned so callers may render a "freshness OK" marker if desired.
 */
import { useMemo } from 'react'
import useControllerState from '@web/hooks/useControllerState'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import { FEATURES } from '@web/constants/features'

export type RailgunChainRowStatus =
  | 'ok'
  | 'scanning'
  | 'fetching'
  | 'stale'
  | 'error'
  | 'poisoned'

type SwChain = {
  chainId: number
  fetchStatus: 'idle' | 'fetching' | 'ok' | 'stale' | 'error' | 'timeout'
  scanStatus: 'idle' | 'merkleScanning' | 'txidScanning' | 'complete'
  cachePoisoned: boolean
  lastSyncedAt: number
}

type SwSnapshot = {
  chains: Record<string, SwChain>
}

function reduceChainStatus(c: SwChain): RailgunChainRowStatus {
  if (c.cachePoisoned) return 'poisoned'
  if (c.scanStatus === 'merkleScanning' || c.scanStatus === 'txidScanning') return 'scanning'
  if (c.fetchStatus === 'fetching') return 'fetching'
  if (c.fetchStatus === 'error' || c.fetchStatus === 'timeout') return 'error'
  if (c.fetchStatus === 'stale') return 'stale'
  if (c.fetchStatus === 'ok') return 'ok'
  // idle without a prior sync → treat as stale so UI signals "not yet known".
  return c.lastSyncedAt > 0 ? 'stale' : 'stale'
}

export function useRailgunChainStatus(chainId: number | bigint | undefined): RailgunChainRowStatus | null {
  const railgunBgState = useControllerState('RailgunBackgroundController')
  const { account } = useSelectedAccountControllerState()
  return useMemo(() => {
    if (!FEATURES.railgunPerChainSync) return null
    if (!account?.addr || chainId == null) return null
    const id = Number(chainId)
    if (!Number.isFinite(id)) return null
    const snapshots = (railgunBgState as any)?.accountSnapshots as
      | Record<string, SwSnapshot>
      | undefined
    if (!snapshots) return null
    const snap = snapshots[account.addr.toLowerCase()]
    if (!snap?.chains) return null
    // chrome.storage serialization stringifies numeric keys, runtime stays numeric.
    const chain = (snap.chains as any)[id] ?? (snap.chains as any)[String(id)]
    if (!chain) return null
    return reduceChainStatus(chain as SwChain)
  }, [railgunBgState, account?.addr, chainId])
}
